import * as React from 'react'
import { useId } from 'react'
import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { clsx } from 'clsx'

import { CheckIcon, MinusIcon } from '../icons'
import { Text } from '../Text'

import styles from './Checkbox.module.css'
import iconSlotStyles from '../../styles/atoms/icon-slot.module.css'

type CheckboxElement = React.ElementRef<typeof CheckboxPrimitive.Root>

type CheckboxProps = Omit<
  CheckboxPrimitive.CheckboxProps,
  'onCheckedChange' | 'onChange'
> & {
  /**
   * Controlled checked state for the checkbox.
   */
  checked?: CheckboxPrimitive.CheckedState
  /**
   * Initial checked state for uncontrolled usage.
   * @default false
   */
  defaultChecked?: CheckboxPrimitive.CheckedState
  /**
   * Class name applied to the outer checkbox-and-label container.
   */
  className?: string
  /**
   * Label of the checkbox. When provided, a label element is rendered.
   */
  children?: React.ReactNode
  /**
   * Callback when the checkbox changes between checked, unchecked, or indeterminate state.
   */
  onChange?: (checked: CheckboxPrimitive.CheckedState) => void
}

/**
 * Base input element to toggle an option on and off.
 */
const Checkbox = React.forwardRef<CheckboxElement, CheckboxProps>(
  (
    {
      id: passedId,
      className,
      checked,
      children,
      onChange,
      ...props
    }: CheckboxProps,
    forwardedRef,
  ) => {
    const generatedId = useId()
    const id = passedId || generatedId

    const control = (
      <CheckboxPrimitive.Root
        {...props}
        id={id}
        checked={checked}
        ref={forwardedRef}
        className={clsx(styles.checkbox)}
        onCheckedChange={onChange}
      >
        <CheckboxPrimitive.Indicator asChild>
          {checked === 'indeterminate' ? (
            <MinusIcon stroke={3} size="xsmall" />
          ) : (
            <CheckIcon stroke={3} size="xsmall" />
          )}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
    )

    return (
      <div
        className={clsx(
          styles.container,
          children && styles.withLabel,
          className,
        )}
      >
        {children ? (
          <span className={iconSlotStyles.firstLine}>{control}</span>
        ) : (
          control
        )}

        {children && (
          <Text as="label" htmlFor={id}>
            {children}
          </Text>
        )}
      </div>
    )
  },
)

Checkbox.displayName = 'Checkbox'

type CheckboxGroupProps = React.HTMLAttributes<HTMLFieldSetElement> & {
  label: React.ReactNode
}

const CheckboxGroup = React.forwardRef<HTMLFieldSetElement, CheckboxGroupProps>(
  (
    { label, children, className, ...props }: CheckboxGroupProps,
    forwardedRef,
  ) => {
    return (
      <fieldset
        ref={forwardedRef}
        className={clsx(styles.group, className)}
        {...props}
      >
        <Text as="legend" weight="bold" className={styles.legend}>
          {label}
        </Text>
        {children}
      </fieldset>
    )
  },
)

CheckboxGroup.displayName = 'Checkbox.Group'

const CheckboxObject = Object.assign(Checkbox, {
  Group: CheckboxGroup,
})

export { CheckboxObject as Checkbox }
export type { CheckboxProps }
